import type { EnergyConsumerResult } from '../../../..';
import type {
  EnergyConsumerEvaluatedVoltageDiffTransformerEndPercent,
  EnergyConsumerEvaluatedVoltageDiffTransformerEnd,
  EnergyConsumerEvaluatedVoltages,
  EnergyConsumerEvaluatedVoltageCosinePhi
} from '../../../../derived-state/loadFlowSummary';
import { NumbersRange } from '../../../../domain/NumbersRange';
import { Messenger } from '../../../AttentionStatusRepository';
import type { ForEnergyConsumerEvaluation } from './forEnergyConsumerEvaluation';

export class EnergyConsumerSummaryEvaluation {
  private energyConsumerResults: EnergyConsumerResult[];
  private evaluationEngine: ForEnergyConsumerEvaluation;

  constructor(
    energyConsumerResults: EnergyConsumerResult[],
    evaluationEngine: ForEnergyConsumerEvaluation
  ) {
    this.energyConsumerResults = energyConsumerResults;
    this.evaluationEngine = evaluationEngine;
  }

  evaluateVoltageDiffPercent(): EnergyConsumerEvaluatedVoltageDiffTransformerEndPercent {
    const absoluteVoltageDiffsPercent = this.energyConsumerResults.map(result =>
      Math.abs(result.voltageDiffTransformerEndPercent)
    );

    const voltageDiffPercentRange = new NumbersRange(
      absoluteVoltageDiffsPercent
    );
    const min = this.evaluationEngine.evaluateVoltageDiffTransformerEndPercent(
      voltageDiffPercentRange.min,
      Messenger.notDelegated()
    );
    const max = this.evaluationEngine.evaluateVoltageDiffTransformerEndPercent(
      voltageDiffPercentRange.max,
      Messenger.notDelegated()
    );

    return { min, max };
  }

  evaluateVoltageDiffNominal(): EnergyConsumerEvaluatedVoltageDiffTransformerEnd {
    const voltageDiffNominals = this.energyConsumerResults.map(
      e => e.voltageDiffNominal
    );

    const voltageDiffNominalRange = new NumbersRange(voltageDiffNominals);
    const min = this.evaluationEngine.evaluateVoltageDiffNominal(
      voltageDiffNominalRange.min,
      Messenger.notDelegated()
    );
    const max = this.evaluationEngine.evaluateVoltageDiffNominal(
      voltageDiffNominalRange.max,
      Messenger.notDelegated()
    );

    return { min, max };
  }

  evaluateVoltages(): EnergyConsumerEvaluatedVoltages {
    const voltages = this.energyConsumerResults.map(e => e.voltage);

    const voltageRange = new NumbersRange(voltages);
    const min = this.evaluationEngine.evaluateVoltage(
      voltageRange.min,
      Messenger.notDelegated()
    );
    const max = this.evaluationEngine.evaluateVoltage(
      voltageRange.max,
      Messenger.notDelegated()
    );

    return { min, max };
  }

  evaluateVoltageDiffTransformerEnd(): EnergyConsumerEvaluatedVoltageDiffTransformerEnd {
    const voltageDiffTransformerEnds = this.energyConsumerResults.map(
      e => e.voltageDiffTransformerEnd
    );

    const voltageDiffNominalRange = new NumbersRange(
      voltageDiffTransformerEnds
    );
    const min = this.evaluationEngine.evaluateVoltageDiffTransformerEnd(
      voltageDiffNominalRange.min,
      Messenger.notDelegated()
    );
    const max = this.evaluationEngine.evaluateVoltageDiffTransformerEnd(
      voltageDiffNominalRange.max,
      Messenger.notDelegated()
    );

    return { min, max };
  }

  evaluateVoltageCosinePhi(): EnergyConsumerEvaluatedVoltageCosinePhi {
    const voltageCosinePhis = this.energyConsumerResults.map(
      e => e.voltageCosinePhi
    );

    const voltageCosinePhiRange = new NumbersRange(voltageCosinePhis);
    const min = this.evaluationEngine.evaluateVoltageCosinePhi(
      voltageCosinePhiRange.min,
      Messenger.notDelegated()
    );
    const max = this.evaluationEngine.evaluateVoltageCosinePhi(
      voltageCosinePhiRange.max,
      Messenger.notDelegated()
    );

    return { min, max };
  }
}
